/**
 * Which lens EACH of a record's relationships opens in — resolved by the SERVER
 * (#2276, per-slot since the owner correction).
 *
 * ── WHY THIS IS NOT A CLIENT DECISION ──────────────────────────────────────
 * Before this, the lens was a browser constant and a stored preference, so:
 *
 *   · no deployment could offer or withhold a lens;
 *   · no administrator could say which one a record type opens in;
 *   · and nothing could say WHICH RUNG decided, because the client never sees
 *     the rows that lost. `apps/console/src/app/Nav.tsx` already refuses to
 *     re-implement a cascade for exactly that reason, in writing.
 *
 * So the host supplies the one fact it owns — which registered slots are on
 * screen and what each of them draws — and `core.resolve_related_presentation`
 * answers the rest in ONE round trip, per slot: the offer, the governed default,
 * the active choice, the target's map recipe, and the deciding rung.
 *
 * ── ONE CALL, MANY ANSWERS ─────────────────────────────────────────────────
 * Asking per section would be N round trips for a document the server reads
 * once, and would let two sections of one page disagree about the source's own
 * resolution. Asking once and answering once was the shape this replaced, and it
 * meant one address group could drag an entire record onto a canvas.
 *
 * The browser keeps one half, deliberately: which lenses THIS BUILD can draw.
 * That is a property of the bundle, not of the deployment, and asking the
 * database about it would be asking the wrong process
 * (`resolveRelatedPageEnvelope` is where the two meet).
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  GRAPH_LAYOUTS, RELATED_PRESENTATIONS, resolveRelatedPageEnvelope, resolveSlotPresentation,
  type GraphLayout, type HostResolvedRelatedPage, type HostResolvedSlotPresentation,
  type HostResolvedWorkspace, type RelatedPresentation,
} from '@solum/experience'
import { useSolum } from '../data/client'
import { CONSOLE_HOST } from '../data/queries'

/**
 * The lenses this console build actually has a renderer for.
 *
 * All four, today — and it is still stated rather than assumed, because the
 * moment a host ships without one the contract is that it NAMES the lens as
 * unsupported and draws the next one, and a host that claimed everything could
 * never exercise that path.
 */
export const CONSOLE_RELATED_LENSES: readonly RelatedPresentation[] = RELATED_PRESENTATIONS

/**
 * The graph layouts this console build can actually place (#2324).
 *
 * Both, today — and it is still STATED rather than assumed, for the reason the
 * lens list beside it gives: the contract is that a host NAMES an offered layout
 * it cannot draw and uses the next one, and a host that claimed everything could
 * never exercise that path. A portal that ships no canvas passes `[]` here and
 * has Graph withdrawn entirely, which is the one refusal that cannot be wrong.
 */
export const CONSOLE_GRAPH_LAYOUTS: readonly GraphLayout[] = GRAPH_LAYOUTS

/** One registered relationship slot, as the host sees it on screen. */
export interface RelatedSlotRef {
  /** `<relType>:<direction>:<otherEntity>` — `RelationSlot.id`. */
  slot: string
  /** The far entity this slot draws. Declared, never parsed out of the id. */
  target: string
}

export interface RelatedPresentationState {
  page: HostResolvedRelatedPage
  /**
   * The WHOLE-RECORD reading: List or Graph, and Network or Metro inside Graph.
   *
   * It arrives on the SAME envelope as the slots because the door resolves this
   * entity's document exactly once for the page — two doors would resolve the
   * same ladder twice, in two round trips, and could disagree about a page that
   * is on screen once.
   */
  workspace: HostResolvedWorkspace
  /**
   * The governed answer for one slot. Never undefined: a slot the server did not
   * answer for resolves to the Grid with its reason attached, which is also the
   * state during the first paint of every record.
   */
  of: (slot: string) => HostResolvedSlotPresentation
  /** The governed answer has arrived (or failed) — the lens is not a guess yet. */
  ready: boolean
}

export function useRelatedPresentation(
  entity: string,
  slots: readonly RelatedSlotRef[],
  requested: Record<string, RelatedPresentation>,
  /**
   * This person's WHOLE-RECORD request, from `relationships.workspace`.
   *
   * A separate parameter from `requested`, deliberately and all the way down:
   * the ruling forbids reinterpreting a per-slot lens as this choice, and two
   * values that never share a container cannot be confused by a later edit.
   */
  workspace: { mode?: string; layout?: string } | null = null,
): RelatedPresentationState {
  const { client } = useSolum()
  // Sorted and deduped by slot identity so a re-render that reorders the same
  // sections is a cache HIT rather than a fresh round trip — the same reason
  // `useRecordTitles` does it, and it matters more here because the answer
  // drives what is on screen.
  const wanted = useMemo(() => {
    const byId = new Map<string, RelatedSlotRef>()
    for (const ref of slots) {
      if (ref.slot && ref.target && !byId.has(ref.slot)) byId.set(ref.slot, ref)
    }
    return [...byId.values()].sort((a, b) => a.slot.localeCompare(b.slot))
  }, [slots])

  // Stable across renders that produce an equal object: react-query hashes the
  // key, and an inline object literal with the same contents hashes the same —
  // but the REQUEST is rebuilt per render by `requestedFor`, so it is normalised
  // here rather than trusted to be referentially stable.
  const ask = useMemo(() => {
    const out: Record<string, RelatedPresentation> = {}
    for (const key of Object.keys(requested).sort()) out[key] = requested[key]!
    return out
  }, [requested])

  const askedWorkspace = useMemo(
    () => (workspace && (workspace.mode || workspace.layout) ? workspace : null),
    [workspace])

  const query = useQuery({
    queryKey: ['related-presentation', entity, wanted, ask, askedWorkspace],
    /* NOT gated on `isLive`. The fixture transport answers this door exactly as
       the server does — that is what makes the browser matrix a proof of the
       console rather than of a mock — and gating on it meant the query never
       ran in fixture mode, so every surface fell back to the Grid with no map
       at all. Nine browser cases failed on it, which is how it was found;
       `useResolvedExperience` beside it has never had the gate. */
    enabled: !!entity,
    staleTime: 60_000,
    /* Keep the PREVIOUS answer while a new one is in flight.
     *
     * A person's choice is part of this query's key, so choosing a lens starts a
     * new query — and without this the host reads "no answer yet", which resolves
     * to Grid alone. A section offering one lens draws no selector, so the
     * control the person had just used UNMOUNTED under their hand and came back
     * a moment later as new DOM. Keyboard focus went to `document.body` with it,
     * which is a keyboard user being thrown to the top of the page for pressing
     * an arrow key.
     *
     * Measured, not reasoned about: the `<section>` and its `<h3>` survived the
     * change and every `button[role=radio]` inside came back new. The previous
     * answer is the right thing to show anyway — it is what is still on screen. */
    placeholderData: (previous: unknown) => previous,
    queryFn: () => client.raw().rpcScalar<unknown>('resolve_related_presentation', {
      p_from_entity: entity,
      p_slots: wanted,
      p_persona: null,
      p_host: CONSOLE_HOST,
      p_requested: Object.keys(ask).length > 0 ? ask : null,
      p_workspace: askedWorkspace,
    }, 'core'),
  })

  const settled = !query.isLoading || query.isError
  const page = useMemo(
    // An absent or failed answer resolves every section to the Grid with its
    // reason attached — which is also the state during the first paint of every
    // record, so it is the ordinary case rather than a defensive crouch.
    () => resolveRelatedPageEnvelope(query.data, CONSOLE_RELATED_LENSES, CONSOLE_GRAPH_LAYOUTS),
    [query.data])

  const of = useMemo(() => (slot: string): HostResolvedSlotPresentation =>
    page.slots[slot] ?? resolveSlotPresentation(slot, null, CONSOLE_RELATED_LENSES),
  [page])

  return { page, workspace: page.workspace, of, ready: settled }
}
